import React from 'react';
import PropTypes from 'prop-types';
import { Tag, Spacer, Flexer, StaticTooltip, Title } from '@ontruck/orc';

import { FormatPrice } from 'utils/format';

import {
  TENDER_EXTRA,
  EXTRA_NUMERIC,
  EXTRA_PERCENT,
  NEGOTIATED_EXTRA,
  MANUAL_EXTRA,
} from 'modules/shipper-order/Form/constants';

import { StyledContent, StyledContainer, StyledTitle } from '../styled';
import { injectIntl } from 'react-intl';

const applyExtras = (base = 0, extras = []) => {
  const ratio = extras
    .filter(({ type }) => type === EXTRA_PERCENT)
    .reduce((acc, { amount }) => acc + (Number(amount) || 0) / 100, 1);

  const bonus = extras
    .filter(({ type }) => type === EXTRA_NUMERIC)
    .reduce((acc, { amount }) => acc + (Number(amount) || 0), 0);

  return Number(base) * Number(ratio) + Number(bonus);
};

const removeFromSummary = [TENDER_EXTRA, NEGOTIATED_EXTRA, MANUAL_EXTRA];

const SummarySection = ({
  quote,
  currency,
  title,
  insideTitle,
  previousNetValue,
  maxWidth = 344,
  intl,
}) => {
  const t = intl.messages.modules.journey.editForm.summary_section;
  const extraList = quote.extra_costs || [];
  const vat_full_value = quote.vat_full_cost;
  const net_full_value = quote.net_full_cost;
  const net_price = quote.net_cost;
  const total_value = Number(net_full_value) + Number(vat_full_value);
  const extras = (extraList || [])
    .filter((e) => !e.include_in_base)
    .filter(({ concept }) => !removeFromSummary.includes(concept));

  const isManual = !!(extraList || []).find(({ concept }) => concept === MANUAL_EXTRA);
  const isContract = quote.is_contract;
  const isTender = !!(extraList || []).find(({ concept }) => concept === TENDER_EXTRA);
  const isNegotiated = !!(extraList || []).find(({ concept }) => concept === NEGOTIATED_EXTRA);

  const base = applyExtras(
    net_price,
    extraList.filter((e) => e.include_in_base)
  );

  return (
    <StyledContainer maxWidth={maxWidth}>
      <StyledTitle>{title}</StyledTitle>
      <StyledContent p="S">
        {insideTitle && (
          <Spacer mBottom="S">
            <Title text={insideTitle} size="M" level="h3" />
          </Spacer>
        )}
        <Spacer mBottom="S">
          <Flexer.Parent>
            <Flexer.Child flexGrow="1">
              {isTender && t.base_tender}
              {isNegotiated && t.base_negotiated}
              {isManual && t.base_manual}
              {!isTender && !isNegotiated && !isManual && t.base}
              {isContract && (
                <>
                  &nbsp;&nbsp;
                  <Tag
                    text={t.contract}
                    skin="modernGrey"
                    textTransform="uppercase"
                    weight="bold"
                    size="S"
                  />
                </>
              )}
            </Flexer.Child>
            <Flexer.Child>
              <FormatPrice currency={currency} value={base} />
            </Flexer.Child>
          </Flexer.Parent>
        </Spacer>
        {extras.map(({ concept, net, ...extra }) => (
          <Spacer mBottom="S" key={concept}>
            <Flexer.Parent>
              <Flexer.Child flexGrow="1">
                {t.extra_names[concept] ? t.extra_names[concept] : concept}
              </Flexer.Child>
              <Flexer.Child>
                <FormatPrice currency={currency} value={net || 0} />
              </Flexer.Child>
            </Flexer.Parent>
          </Spacer>
        ))}

        <Spacer mBottom="L" mTop={(extras.length && 'L') || 'N'}>
          <Flexer.Parent>
            <Flexer.Child flexGrow="1">
              <strong>{t.total_no_vat}</strong>
            </Flexer.Child>
            <Flexer.Child>
              {(previousNetValue && previousNetValue !== net_full_value && (
                <StaticTooltip
                  component={
                    <div>
                      {t.was} <FormatPrice currency={currency} value={previousNetValue} />
                    </div>
                  }
                  skin="warning"
                  position="right"
                  size="M"
                  weight="bold"
                >
                  <FormatPrice currency={currency} value={net_full_value} />
                </StaticTooltip>
              )) || <FormatPrice currency={currency} value={net_full_value} />}
            </Flexer.Child>
          </Flexer.Parent>
        </Spacer>

        <Spacer mBottom="S">
          <Flexer.Parent>
            <Flexer.Child flexGrow="1">{t.vat}</Flexer.Child>
            <Flexer.Child>
              <FormatPrice currency={currency} value={vat_full_value} />
            </Flexer.Child>
          </Flexer.Parent>
        </Spacer>
        <Spacer>
          <Flexer.Parent>
            <Flexer.Child flexGrow="1">{t.total}</Flexer.Child>
            <Flexer.Child>
              <FormatPrice currency={currency} value={total_value} />
            </Flexer.Child>
          </Flexer.Parent>
        </Spacer>
      </StyledContent>
    </StyledContainer>
  );
};

SummarySection.propTypes = {
  currency: PropTypes.string,
  title: PropTypes.object,
  insideTitle: PropTypes.object,
  maxWidth: PropTypes.string,
  quote: PropTypes.object,
  previousNetValue: PropTypes.string,
};

SummarySection.defaultProps = {
  maxWidth: '344px',
  title: null,
  insideTitle: null,
};

export default injectIntl(SummarySection);
